import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../../components/SortableItem";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";


const ViewSubscriberOrder = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tradepro"); // "tradepro" or "streetfolio"
  const [searchParams] = useSearchParams();
  // Tab-specific states
  const [tradeproData, setTradeproData] = useState({
    items: [],
    rowOrder: "",
    loading: false,
    error: null,
    saving: false
  });

  const [streetfolioData, setStreetfolioData] = useState({
    items: [],
    rowOrder: "",
    loading: false,
    error: null,
    saving: false
  });

  const toastShownRef = useRef(false);
  const token = localStorage.getItem("authToken");

  // Determine API endpoints and navigation based on active tab
  const getApiEndpoints = () => {
    if (activeTab === "tradepro") {
      return {
        fetch: "workflowSubscriber",
        save: "workflowSubscriber/save-order",
        mainPage: "/Workflow/Subscriber",
        managePage: "Manager Order Subscriber Workflow - Tradepro"
      };
    } else {
      return {
        fetch: "workflowSubscriberStreet",
        save: "workflowSubscriberStreet/save-order",
        mainPage: "/Workflow/Subscriber",
        managePage: "Manager Order Subscriber Workflow - Streetfolio"
      };
    }
  };

  // Fetch workflow subscriber order data from API
  const fetchWorkflowSubscriberOrder = async (tab) => {
    const endpoints = tab === "tradepro" ? {
      fetch: "workflowSubscriber",
      save: "workflowSubscriber/save-order"
    } : {
      fetch: "workflowSubscriberStreet",
      save: "workflowSubscriberStreet/save-order"
    };

    try {
      if (tab === "tradepro") {
        setTradeproData(prev => ({ ...prev, loading: true, error: null }));
      } else {
        setStreetfolioData(prev => ({ ...prev, loading: true, error: null }));
      }

      const response = await axios.get(`${API_URL}${endpoints.fetch}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.status === 200 && response.data.success) {
        const workflowData = response.data.data.data || [];
        // Transform the data to match the expected format for drag and drop
        const transformedData = workflowData.map((item) => ({
          id: item.id,
          title: item.workflow_name,
          sortid: item.sortid,
          event_type: item.event_type,
          scheduletype: item.scheduletype,
          scheduletime: item.scheduletime,
          check_condition: item.check_condition,
        }));

        // Sort by sortid if available
        transformedData.sort((a, b) => a.sortid - b.sortid);

        if (tab === "tradepro") {
          setTradeproData(prev => ({ ...prev, items: transformedData }));
        } else {
          setStreetfolioData(prev => ({ ...prev, items: transformedData }));
        }

        if (!toastShownRef.current) {
          showToast.success(response.data.message);
          toastShownRef.current = true;
        }
      } else {
        const errorMessage = response.data.message || "Failed to fetch workflow subscriber order";
        if (tab === "tradepro") {
          setTradeproData(prev => ({ ...prev, error: errorMessage }));
        } else {
          setStreetfolioData(prev => ({ ...prev, error: errorMessage }));
        }
        showToast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching workflow subscriber order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while fetching data";

      if (tab === "tradepro") {
        setTradeproData(prev => ({ ...prev, error: errorMessage }));
      } else {
        setStreetfolioData(prev => ({ ...prev, error: errorMessage }));
      }
      showToast.error(errorMessage);
    } finally {
      if (tab === "tradepro") {
        setTradeproData(prev => ({ ...prev, loading: false }));
      } else {
        setStreetfolioData(prev => ({ ...prev, loading: false }));
      }
    }
  };

  // Fetch data on component mount and when tab changes
  useEffect(() => {
    fetchWorkflowSubscriberOrder("tradepro");
    fetchWorkflowSubscriberOrder("streetfolio");
  }, []);
  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'streetfolio') {
      setActiveTab('streetfolio');
    } else if (type === 'tradepro') {
      setActiveTab('tradepro');
    }
  }, [searchParams]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event, tab) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      if (tab === "tradepro") {
        setTradeproData(prev => {
          const oldIndex = prev.items.findIndex((item) => item.id === active.id);
          const newIndex = prev.items.findIndex((item) => item.id === over.id);
          return {
            ...prev,
            items: arrayMove(prev.items, oldIndex, newIndex)
          };
        });
      } else {
        setStreetfolioData(prev => {
          const oldIndex = prev.items.findIndex((item) => item.id === active.id);
          const newIndex = prev.items.findIndex((item) => item.id === over.id);
          return {
            ...prev,
            items: arrayMove(prev.items, oldIndex, newIndex)
          };
        });
      }
    }
  };

  // Save order function with axios POST request
  const saveOrder = async (tab) => {
    const endpoints = getApiEndpoints();
    let currentItems = [];

    if (tab === "tradepro") {
      currentItems = tradeproData.items;
      if (currentItems.length === 0) {
        showToast.error("No items to save");
        return;
      }
      setTradeproData(prev => ({ ...prev, saving: true }));
    } else {
      currentItems = streetfolioData.items;
      if (currentItems.length === 0) {
        showToast.error("No items to save");
        return;
      }
      setStreetfolioData(prev => ({ ...prev, saving: true }));
    }

    try {
      const selectedOrder = currentItems.map((item) => item.id);

      if (tab === "tradepro") {
        setTradeproData(prev => ({ ...prev, rowOrder: selectedOrder.join(",") }));
      } else {
        setStreetfolioData(prev => ({ ...prev, rowOrder: selectedOrder.join(",") }));
      }

      // Prepare the data payload
      const payload = {
        row_order: selectedOrder.join(","),
        items: selectedOrder,
      };

      const response = await axios.post(
        `${API_URL}${endpoints.save}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      if (response.data.status === 200 || response.status === 200) {
        showToast.success(response.data.message || "Order saved successfully!");
      } else {
        showToast.error(response.data.message || "Failed to save order");
      }
    } catch (error) {
      console.error("Error saving order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving order. Please try again.";
      showToast.error(errorMessage);
    } finally {
      if (tab === "tradepro") {
        setTradeproData(prev => ({ ...prev, saving: false }));
      } else {
        setStreetfolioData(prev => ({ ...prev, saving: false }));
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getCurrentData = () => {
    return activeTab === "tradepro" ? tradeproData : streetfolioData;
  };

  const endpoints = getApiEndpoints();
  const currentData = getCurrentData();

  // Loading state for current tab
  if (currentData.loading) {
    return (
      <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
        <div className="">
          <div className="panel panel-default with-rounded-corner with-shadow p-10">
            <div className="panel-heading border-0">
              <div className="row">
                <div className="col-md-6">
                  <h4 className="panel-title pull-left">Manager Order Subscriber Workflow</h4>
                </div>
                <div className="col-md-6" style={{ textAlign: "right" }}>
                  <button
                    onClick={() => navigate(endpoints.mainPage)}
                    className="btn btn-default btn-sm"
                  >
                    <b>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </b>{" "}
                    Return to Workflow Subscribers
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="panel panel-default with-rounded-corner with-shadow mb-3">
              <div className="panel-body">
                <div className="row">
                  <div className="col-md-12">
                    <label className="me-3">Select Workflow Type:</label>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${activeTab === "tradepro" ? "btn-primary" : "btn-default"}`}
                        onClick={() => handleTabChange("tradepro")}
                      >
                        Tradepro Workflows
                      </button>
                      <button
                        type="button"
                        className={`btn ${activeTab === "streetfolio" ? "btn-primary" : "btn-default"}`}
                        onClick={() => handleTabChange("streetfolio")}
                      >
                        Streetfolio Workflows
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center p-4">
              <div>
                <LoadingSpinner />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state for current tab
  if (currentData.error) {
    return (
      <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
        <div className="">
          <div className="panel panel-default with-rounded-corner with-shadow p-10">
            <div className="panel-heading border-0">
              <div className="row">
                <div className="col-md-6">
                  <h4 className="panel-title pull-left">Manager Order Subscriber Workflow</h4>
                </div>
                <div className="col-md-6" style={{ textAlign: "right" }}>
                  <button
                    onClick={() => navigate(endpoints.mainPage)}
                    className="btn btn-default btn-sm"
                  >
                    <b>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </b>{" "}
                    Return to Workflow Subscribers
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="panel panel-default with-rounded-corner with-shadow mb-3">
              <div className="panel-body">
                <div className="row">
                  <div className="col-md-12">
                    <label className="me-3">Select Workflow Type:</label>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${activeTab === "tradepro" ? "btn-primary" : "btn-default"}`}
                        onClick={() => handleTabChange("tradepro")}
                      >
                        Tradepro Workflows
                      </button>
                      <button
                        type="button"
                        className={`btn ${activeTab === "streetfolio" ? "btn-primary" : "btn-default"}`}
                        onClick={() => handleTabChange("streetfolio")}
                      >
                        Streetfolio Workflows
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center p-4">
              <div className="alert alert-danger">
                <strong>Error:</strong> {currentData.error}
              </div>
              <button
                onClick={() => fetchWorkflowSubscriberOrder(activeTab)}
                className="btn btn-primary btn-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">Manager Order Subscriber Workflow</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                <button
                  onClick={() => navigate(endpoints.mainPage)}
                  className="btn btn-default btn-sm"
                  disabled={currentData.saving}
                >
                  <b>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </b>{" "}
                  Return to Workflow Subscribers
                </button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="me-3">Select Workflow Type:</label>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${activeTab === "tradepro" ? "btn-primary" : "btn-default"}`}
                      onClick={() => handleTabChange("tradepro")}
                    >
                      Tradepro Workflows
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === "streetfolio" ? "btn-primary" : "btn-default"}`}
                      onClick={() => handleTabChange("streetfolio")}
                    >
                      Streetfolio Workflows
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="panel panel-default with-rounded-corner with-shadow">
            {currentData.items.length > 0 ? (
              <div className="table-responsive">
                <form name="checkForm">
                  <input
                    type="hidden"
                    name="row_order"
                    id="row_order"
                    value={currentData.rowOrder}
                  />

                  {/* Instructions */}
                  <div className="alert alert-info m-3">
                    <i className="fa fa-info-circle"></i>
                    <strong> Instructions:</strong> Drag and drop the items below
                    to reorder them. Click "Save Order" when you're done.
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, activeTab)}
                  >
                    <SortableContext
                      items={currentData.items}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul
                        id="sortable-row"
                        style={{
                          listStyle: "none",
                          width: "100%",
                          paddingRight: "30px",
                          paddingTop: "30px",
                          paddingLeft: "30px",
                        }}
                      >
                        {currentData.items.map((item, index) => (
                          <SortableItem key={item.id} id={item.id}>
                            <li
                              style={{
                                marginBottom: "10px",
                                padding: "5px",
                                backgroundColor: "#f7f7f7",
                                cursor: "move",
                                borderRadius: "8px",
                                paddingLeft: "15px",
                                border: "1px #c1c1c1 solid",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              }}
                            >
                              <span
                                style={{
                                  backgroundColor: "#007bff",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  marginRight: "15px",
                                }}
                              >
                                {index + 1}
                              </span>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                style={{ color: "#28a745", marginRight: "10px" }}
                              />
                              <span
                                style={{ fontSize: "14px", fontWeight: "500" }}
                              >
                                {item.title}
                              </span>
                              <span
                                style={{
                                  marginLeft: "auto",
                                  color: "#6c757d",
                                  fontSize: "12px",
                                }}
                              >
                                {/* Additional info can be displayed here */}
                              </span>
                            </li>
                          </SortableItem>
                        ))}
                      </ul>
                    </SortableContext>
                  </DndContext>

                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => saveOrder(activeTab)}
                      disabled={currentData.saving || currentData.items.length === 0}
                    >
                      {currentData.saving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-save me-2"></i>
                          Save Order
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="text-center p-5">
                <i className="fa fa-list-ol fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">No {activeTab} workflow subscribers found</h5>
                <p className="text-muted">
                  There are no workflow subscriber items available to order.
                </p>
                <button
                  onClick={() => fetchWorkflowSubscriberOrder(activeTab)}
                  className="btn btn-primary btn-sm"
                >
                  <i className="fa fa-refresh me-1"></i>
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal component */}
      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ViewSubscriberOrder;